/**
 * Day-as-a-list (design: phone calendar and My Day): times, patient, service,
 * place, package context, a Next marker, the Delivered chip, and ghosts
 * labelled in words.
 */

import { Link } from '@tanstack/react-router';
import { useIntl } from 'react-intl';
import s from './scheduling.module.css';
import a from '../access/access.module.css';
import { Chip } from '@/ui/kit';
import { modeLabelKey, type CalendarItem } from './bookingRules';
import { useLocale } from '@/core/i18n/LocaleProvider';
import { formatTime } from '@/core/calendar/format';
import { isGhost, serviceGroup } from './calendarMath';
import { sessionChip } from '../sessions/sessionRules';
import { GROUP_STYLE } from './CalendarGrid';

export function AgendaList(props: {
  items: readonly CalendarItem[];
  tz: string;
  head: string;
  sub?: string;
  nextId?: string | null;
  patientName: (b: CalendarItem) => string;
  staffName: (uid: string) => string;
  roomName: (id: string | null) => string | null;
  showPractitioner: boolean;
  extra?: (b: CalendarItem) => string | null;
  /** Per-row action (e.g. Record delivery / View session) — rendered beside the chip. */
  action?: (b: CalendarItem) => React.ReactNode;
  empty: string;
  /**
   * A quiet line under the caption for ambient state about the list itself
   * (ZOY-321: "this is the last known schedule"). Deliberately not a Notice
   * and not a live region — it is a standing condition, not an event.
   */
  note?: React.ReactNode;
  /**
   * When given, a single-patient row opens the session sheet in place (a
   * button) instead of navigating; a group occurrence always navigates.
   */
  onOpen?: (b: CalendarItem) => void;
}) {
  const intl = useIntl();
  const t = (id: string, v?: Record<string, string | number>) => intl.formatMessage({ id }, v);
  const { locale } = useLocale();
  const time = (iso: string) => formatTime(iso, { locale, timezone: props.tz });
  return (
    <div className={a.sectionCard} style={{ inlineSize: '100%', maxInlineSize: 860 }}>
      <div style={{ padding: '14px 18px 8px' }}>
        <strong style={{ color: 'var(--zc-teal-deep)' }}>{props.head}</strong>
        {props.sub && (
          <p style={{ fontSize: 12.5, color: 'var(--zc-muted)', marginBlockStart: 2 }}>
            {props.sub}
          </p>
        )}
        {props.note && (
          <div
            style={{
              fontSize: 12.5,
              color: 'var(--zc-warn-ink)',
              marginBlockStart: 4,
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            {props.note}
          </div>
        )}
      </div>
      {props.items.length === 0 && (
        <div className={a.row}>
          <span className={a.rowValue} style={{ fontSize: 12.5, color: 'var(--zc-muted)' }}>
            {props.empty}
          </span>
        </div>
      )}
      {props.items.map((b) => {
        const g = GROUP_STYLE[serviceGroup(b.service.name)];
        const ghost = isGhost(b);
        const meta = [
          props.showPractitioner ? props.staffName(b.practitionerUid) : null,
          t(modeLabelKey(b.locationType)),
          props.roomName(b.roomId),
          props.extra?.(b) ?? null,
        ]
          .filter(Boolean)
          .join(' · ');
        const inner = (
          <>
            <span className={s.agendaTime}>
              {time(b.startsAt)}
              <br />
              {time(b.endsAt)}
            </span>
            <span
              className={s.agendaAccent}
              style={{ background: ghost ? 'var(--zc-line-strong)' : g.border }}
            />
            <span className={s.agendaMain}>
              <span className={s.agendaName}>
                {b.group
                  ? `${t('grp.block')} · ${t('grp.count', { n: b.group.participantCount })}`
                  : props.patientName(b)}
              </span>
              <span style={{ display: 'block', fontSize: 12.5 }}>{b.service.name}</span>
              <span className={s.agendaSub}>{meta}</span>
            </span>
            <span className={s.agendaActions}>
              {/* ZOY-551: ONE chip from the server-derived executionStatus. */}
              {b.group ? (
                <Chip
                  kind={
                    b.group.executionStatus === 'in_progress'
                      ? 'info'
                      : b.group.executionStatus === 'cancelled'
                        ? 'quiet'
                        : 'ok'
                  }
                >
                  {t(`grp.exec.${b.group.executionStatus}`)}
                </Chip>
              ) : (
                (() => {
                  const chip = sessionChip(b, new Date(), props.nextId === b.id);
                  return <Chip kind={chip.kind}>{t(chip.key)}</Chip>;
                })()
              )}
              {!b.group && props.action?.(b)}
            </span>
          </>
        );
        const onOpen = props.onOpen;
        if (onOpen && !b.group) {
          return (
            <button
              key={b.id}
              type="button"
              className={[s.agendaRow, s.agendaRowButton].join(' ')}
              style={{ opacity: ghost ? 0.72 : 1 }}
              onClick={() => onOpen(b)}
            >
              {inner}
            </button>
          );
        }
        return (
          <Link
            key={b.id}
            // ZOY-685: a group occurrence opens its own page, once.
            {...(b.group
              ? { to: '/calendar/groups/$groupId' as const, params: { groupId: b.group.id } }
              : { to: '/calendar/bookings/$bookingId' as const, params: { bookingId: b.id } })}
            className={s.agendaRow}
            style={{ opacity: ghost ? 0.72 : 1 }}
          >
            {inner}
          </Link>
        );
      })}
    </div>
  );
}
